import React, { useState } from 'react';

interface RunEntry {
  id: string;
  goal: string;
  status: 'done' | 'failed' | 'running';
  model: string;
  durationMs: number;
  costUsd: number;
  tasks: number;
  ts: number;
}

interface CostBreakdown {
  model: string;
  role: string;
  tokens: number;
  costUsd: number;
}

const MOCK_RUNS: RunEntry[] = [
  {
    id: 'run_4f2a9b',
    goal: 'Add OAuth2 login flow with GitHub provider',
    status: 'done',
    model: 'claude-opus-4-7',
    durationMs: 183200,
    costUsd: 0.48,
    tasks: 7,
    ts: Date.now() - 3 * 60 * 1000,
  },
  {
    id: 'run_3c1e8d',
    goal: 'Fix race condition in WebSocket handler',
    status: 'done',
    model: 'claude-sonnet-4-6',
    durationMs: 61400,
    costUsd: 0.09,
    tasks: 3,
    ts: Date.now() - 28 * 60 * 1000,
  },
  {
    id: 'run_2b0d7c',
    goal: 'Scaffold multi-tenant billing with Stripe',
    status: 'running',
    model: 'claude-opus-4-7',
    durationMs: 97000,
    costUsd: 0.31,
    tasks: 12,
    ts: Date.now() - 97000,
  },
  {
    id: 'run_1a9c6e',
    goal: 'Write integration tests for payments API',
    status: 'failed',
    model: 'claude-haiku-4-5',
    durationMs: 24800,
    costUsd: 0.02,
    tasks: 2,
    ts: Date.now() - 2 * 60 * 60 * 1000,
  },
  {
    id: 'run_0z8b5d',
    goal: 'Migrate PostgreSQL schema to add audit columns',
    status: 'done',
    model: 'claude-sonnet-4-6',
    durationMs: 44100,
    costUsd: 0.07,
    tasks: 4,
    ts: Date.now() - 5 * 60 * 60 * 1000,
  },
];

const MOCK_COST: CostBreakdown[] = [
  { model: 'claude-opus-4-7', role: 'builder', tokens: 48200, costUsd: 0.29 },
  { model: 'claude-opus-4-7', role: 'critic', tokens: 12800, costUsd: 0.08 },
  { model: 'claude-sonnet-4-6', role: 'tester', tokens: 18700, costUsd: 0.06 },
  { model: 'claude-haiku-4-5', role: 'planner', tokens: 9400, costUsd: 0.01 },
];

const MOCK_TIMELINE_ENTRIES = [
  { kind: 'run_start', summary: 'run started', at: 0 },
  { kind: 'plan', summary: 'planned 7 tasks', at: 1200 },
  { kind: 'agent_start', summary: 'builder agent started', at: 2800 },
  { kind: 'tool_call', summary: 'read_file: src/auth/index.ts', at: 14200 },
  { kind: 'diff_proposed', summary: 'oauth2.ts (+142 −0)', at: 38700 },
  { kind: 'critic_review', summary: 'critic: 2 suggestions', at: 52100 },
  { kind: 'diff_applied', summary: 'patch applied: oauth2.ts', at: 67400 },
  { kind: 'test_result', summary: '12/12 tests pass', at: 112600 },
  { kind: 'approval_decision', summary: 'human approved merge', at: 168900 },
  { kind: 'run_end', summary: 'run complete — 0.48 USD', at: 183200 },
];

function fmtDuration(ms: number): string {
  if (ms < 60000) return `${(ms / 1000).toFixed(0)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function fmtAgo(ts: number): string {
  const secs = (Date.now() - ts) / 1000;
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function statusClass(s: RunEntry['status']): string {
  if (s === 'done') return 'health-steady';
  if (s === 'failed') return 'health-hot';
  return 'health-review';
}

function statusLabel(s: RunEntry['status']): string {
  if (s === 'done') return '✓ done';
  if (s === 'failed') return '✗ failed';
  return '⟳ running';
}

function kindColor(k: string): string {
  if (k === 'run_start' || k === 'run_end') return 'var(--mint)';
  if (k === 'test_result') return 'var(--gold)';
  if (k === 'critic_review') return 'var(--lavender)';
  if (k === 'approval_decision') return 'var(--cyan)';
  if (k.startsWith('diff')) return 'var(--coral)';
  return 'var(--muted)';
}

interface RunsViewProps {
  mode: 'beginner' | 'pro';
}

export function RunsView({ mode }: RunsViewProps) {
  const [selectedRunId, setSelectedRunId] = useState<string>(MOCK_RUNS[0]?.id ?? '');
  const [scrubPos, setScrubPos] = useState(9);

  const selectedRun = MOCK_RUNS.find((r) => r.id === selectedRunId) ?? MOCK_RUNS[0];
  const currentEntry = MOCK_TIMELINE_ENTRIES[scrubPos] ?? MOCK_TIMELINE_ENTRIES[0];

  return (
    <div className="shell-grid" style={{ gridTemplateColumns: '320px minmax(0,1fr) 340px' }}>
      <div className="left-column">
        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Run history</span>
            <strong>Recent runs</strong>
          </div>
          <div className="entry-list">
            {MOCK_RUNS.map((run) => (
              <button
                key={run.id}
                type="button"
                className={`run-chip ${selectedRunId === run.id ? 'run-chip-active' : ''}`}
                onClick={() => setSelectedRunId(run.id)}
              >
                <div className="workspace-row">
                  <strong style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)' }}>
                    {run.id}
                  </strong>
                  <span className={`mini-pill ${statusClass(run.status)}`}>
                    {statusLabel(run.status)}
                  </span>
                </div>
                <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: 'var(--text)' }}>
                  {run.goal}
                </p>
                <div
                  style={{
                    marginTop: 8,
                    display: 'flex',
                    gap: 12,
                    fontSize: '0.78rem',
                    color: 'var(--muted)',
                  }}
                >
                  <span>{fmtDuration(run.durationMs)}</span>
                  <span>${run.costUsd.toFixed(2)}</span>
                  <span>{run.tasks} tasks</span>
                  <span>{fmtAgo(run.ts)}</span>
                </div>
              </button>
            ))}
          </div>
          {mode === 'pro' && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                Export bundle
              </button>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                SLSA attestation
              </button>
            </div>
          )}
        </section>
      </div>

      <div className="main-column">
        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Cost &amp; performance</span>
            <strong>{selectedRun?.goal ?? 'Select a run'}</strong>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 12,
              marginBottom: 22,
            }}
          >
            {[
              { label: 'Duration', value: fmtDuration(selectedRun?.durationMs ?? 0) },
              { label: 'Total cost', value: `$${(selectedRun?.costUsd ?? 0).toFixed(2)}` },
              { label: 'Tasks', value: String(selectedRun?.tasks ?? 0) },
              { label: 'Model', value: (selectedRun?.model ?? '').split('/').pop() ?? '' },
            ].map(({ label, value }) => (
              <div
                key={label}
                style={{
                  padding: '14px 16px',
                  borderRadius: 14,
                  border: '1px solid var(--line)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: 6 }}>
                  {label}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '1.1rem',
                    color: 'var(--cyan)',
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: '0.78rem',
                color: 'var(--muted)',
                marginBottom: 10,
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Cost by model + role
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {MOCK_COST.map((row) => {
                const pct = (row.costUsd / 0.44) * 100;
                return (
                  <div key={`${row.model}-${row.role}`} style={{ display: 'grid', gap: 6 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.8rem',
                      }}
                    >
                      <span>
                        <span style={{ color: 'var(--text)' }}>{row.model.split('-')[1]}</span>
                        <span style={{ color: 'var(--muted)', marginLeft: 8 }}>({row.role})</span>
                      </span>
                      <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)' }}>
                        ${row.costUsd.toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        height: 5,
                        borderRadius: 3,
                        background: 'rgba(255,255,255,0.06)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          borderRadius: 3,
                          background: 'linear-gradient(90deg, var(--cyan), var(--mint))',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {mode === 'pro' && (
            <div>
              <div
                style={{
                  fontSize: '0.78rem',
                  color: 'var(--muted)',
                  marginBottom: 10,
                  fontFamily: 'var(--font-mono)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Critical path
              </div>
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: 'rgba(0,0,0,0.25)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.78rem',
                  color: 'var(--muted)',
                  lineHeight: 1.7,
                }}
              >
                {`plan(1.2s) → builder(36.5s) → critic(13.4s) → apply(15.3s) → test(45.2s) → approve(56.3s)`}
                <br />
                <span style={{ color: 'var(--coral)' }}>bottleneck: test runner (45.2s, 24.7% of wall time)</span>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="right-column">
        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Time-travel debugger</span>
            <strong>Scrub any checkpoint</strong>
          </div>

          <div style={{ marginBottom: 16 }}>
            <input
              type="range"
              min={0}
              max={MOCK_TIMELINE_ENTRIES.length - 1}
              value={scrubPos}
              onChange={(e) => setScrubPos(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--cyan)' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.72rem',
                color: 'var(--muted)',
                marginTop: 4,
              }}
            >
              <span>entry 0</span>
              <span>entry {MOCK_TIMELINE_ENTRIES.length - 1}</span>
            </div>
          </div>

          <div
            style={{
              padding: '14px',
              borderRadius: 12,
              border: '1px solid var(--line)',
              marginBottom: 14,
              background: 'rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                color: kindColor(currentEntry?.kind ?? ''),
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              {currentEntry?.kind ?? ''}
            </div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text)' }}>
              {currentEntry?.summary ?? ''}
            </div>
            <div
              style={{
                marginTop: 8,
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                color: 'var(--soft)',
              }}
            >
              +{fmtDuration(currentEntry?.at ?? 0)} from start
            </div>
          </div>

          <div className="entry-list" style={{ maxHeight: 280, overflowY: 'auto' }}>
            {MOCK_TIMELINE_ENTRIES.map((entry, idx) => (
              <button
                key={idx}
                type="button"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: `1px solid ${idx === scrubPos ? 'rgba(40,215,255,0.4)' : 'transparent'}`,
                  background:
                    idx === scrubPos ? 'rgba(40,215,255,0.08)' : 'rgba(255,255,255,0.01)',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 10,
                  alignItems: 'baseline',
                }}
                onClick={() => setScrubPos(idx)}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    color: kindColor(entry.kind),
                    minWidth: 80,
                  }}
                >
                  {entry.kind}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{entry.summary}</span>
              </button>
            ))}
          </div>

          {mode === 'pro' && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                Fork here
              </button>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                Export HTML
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
